import z from "zod";

const legacyMvfPoint = z.object({
  dollar: z.number(),
  value: z.number(),
});

export type LegacyMvfPoint = z.infer<typeof legacyMvfPoint>;

export const legacyMvf = z.array(legacyMvfPoint);

// LegacyMVF doesn't have `placedManually`.
export type LegacyMVF = z.infer<typeof legacyMvf>;

export interface MinimalEvaluation {
  mvf: LegacyMVF;
  fdv: number;
  evaluableId: string;
}

const linearInterpolation = (a: number, b: number, t: number) =>
  a * (1 - t) + b * t;

export const getDollars = (
  mv: number,
  mvf: LegacyMVF,
  maxDollars: number,
): [number, number] => {
  if (mv < 0) throw new Error("Invalid marginal value");
  if (mv > getFDV(mvf)) return [0, 0];
  let multiMes = mvf.filter((point) => point && point.value === mv);
  if (multiMes.length > 0)
    return [multiMes[0]!.dollar, multiMes[multiMes.length - 1]!.dollar];

  const higherPoint = [...mvf]
    .reverse()
    .find((point) => point && point.value > mv);
  if (higherPoint === undefined) return [0, 0];

  const lowerPoint = mvf.find((point) => point && point.value < mv);
  if (lowerPoint === undefined)
    return [higherPoint.dollar, higherPoint.dollar + 1];

  let val = Math.min(
    linearInterpolation(
      lowerPoint.dollar,
      higherPoint.dollar,
      (mv - lowerPoint.value) / (higherPoint.value - lowerPoint.value),
    ),
    maxDollars,
  );
  return [val, val];
};

export const getFDV = (mvf: LegacyMVF) => getMarginalValue(0, mvf);

export const getLDT = (mvf: LegacyMVF): number => {
  const lastPoint = mvf[mvf.length - 1];
  return lastPoint
    ? lastPoint.value === 0
      ? lastPoint.dollar
      : lastPoint.dollar + 1
    : 0;
};

export function getFullness(mvf: LegacyMVF): number {
  const ldt = getLDT(mvf);
  if (ldt === 0 || getFDV(mvf) === 0) return 0;

  // sample at least 100 times
  const dollars = Array.from(Array(100).keys()).map((x) => (x * ldt) / 100);
  const reducer = (area: number, dollar: number) =>
    area + getMarginalValue(dollar, mvf) * (ldt / 100);

  return dollars.reduce(reducer, 0) / (ldt * getFDV(mvf));
}

export function getMarginalValue(
  x: number,
  mvf: LegacyMVF,
  secondPoint: boolean = false,
): number {
  if (x < 0) throw new Error("invalid dollar value");
  if (x > getLDT(mvf)) return 0;

  // The dollar value is either exactly one of the points on the piecewise-
  // linear curve (in which case return the corresponding utility), is
  // between two points on the piecewise curve (in which case
  // linearly interpolate), or is after the last point (in which case return
  // zero).
  if (!secondPoint) {
    const maybeMe = mvf.find((point) => point && point.dollar === x);
    if (maybeMe != undefined) return maybeMe.value;
  } else {
    for (let i = 0; i < mvf.length; i++) {
      const point = mvf[i];
      if (point.dollar !== x) {
        continue;
      }

      // if it's the last point
      if (i === mvf.length - 1) {
        return point.value;
      }

      const nextPoint = mvf[i + 1];
      if (point.dollar === nextPoint.dollar) {
        return nextPoint.value;
      }

      return point.value;
    }
  }

  const rightwardPoint = mvf.find((point) => point && point.dollar > x);
  if (!rightwardPoint) return 0;

  const leftwardPoint = [...mvf]
    .reverse()
    .find((point) => point && point.dollar < x);
  if (!leftwardPoint) return rightwardPoint.value;

  return linearInterpolation(
    leftwardPoint.value,
    rightwardPoint.value,
    (x - leftwardPoint.dollar) / (rightwardPoint.dollar - leftwardPoint.dollar),
  );
}
